// GtNoticeModal — "Tell the candidate?"
//
// Candidates are only emailed when a person chooses to. A move that changes
// what a candidate sees in their portal raises a notice on the server; this
// box shows the exact email that would go, and asks: Send, Don't send, or
// Decide later (closing the box leaves it open on Home).
//
// Used by the board after a move, by Home's "Candidate not yet told" lane, and
// by the candidate record. Pass a list of notice ids to work through several
// in a row; onClose fires once, after the last.
import { useState, useEffect, useCallback } from 'react';

export default function GtNoticeModal({ ids, authFetch, onClose, onDecided }) {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  const [i, setI] = useState(0);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const id = list[i];

  const load = useCallback(async () => {
    setNotice(null); setError(null); setResult(null);
    try { setNotice(await authFetch('/api/notices/' + id)); }
    catch (e) { setError(e.message); }
  }, [authFetch, id]);

  useEffect(() => { if (id) load(); }, [id, load]);

  const next = useCallback(() => {
    if (i + 1 < list.length) setI(i + 1);
    else onClose && onClose();
  }, [i, list.length, onClose]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) next(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, next]);

  async function decide(action) {
    setBusy(true);
    try {
      const r = await authFetch('/api/notices/' + id + '/' + action, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      setResult({ ok: true, text: action === 'send' ? 'Sent to ' + ((notice.email && notice.email.to) || 'the candidate') + '.' : 'Not sent. Nothing went to the candidate.' });
      onDecided && onDecided(r.notice);
      setTimeout(next, 900);
    } catch (e) {
      setResult({ ok: false, text: e.message });
      onDecided && onDecided(null);
    }
    setBusy(false);
  }

  if (!id) return null;
  const email = notice && notice.email;
  const pending = notice && notice.state === 'pending';
  const blocked = email && email.blocked;
  const canSend = pending && email && !blocked && email.sending_enabled;

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && next()}>
      <div className="gtc-modal gtnt-modal" role="dialog" aria-modal="true" aria-labelledby="gtnt-title" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head" id="gtnt-title">
          {notice ? 'Tell ' + (notice.candidate.name || 'the candidate') + '?' : 'Tell the candidate?'}
          {list.length > 1 && <span className="gtnt-count">{i + 1} of {list.length}</span>}
        </div>

        <div className="gtc-modal-body gtnt-body">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
          {!notice && !error && <div className="gtnt-loading">Loading the email…</div>}

          {notice && (
            <>
              <div className="gtnt-change">
                <span className="gtnt-role">{notice.role.title}</span>
                <span className="gtnt-step">
                  {notice.previous_label && <span className="gtnt-from">{notice.previous_label}</span>}
                  {notice.previous_label && <span className="gtnt-arrow" aria-hidden="true">→</span>}
                  <span className="gtnt-to">{notice.status_label}</span>
                </span>
              </div>

              {!pending && (
                <div className="gtnt-banner gtnt-banner--slate">
                  {notice.state === 'sent' && 'This update has already been sent' + (notice.decided_by_name ? ' by ' + notice.decided_by_name : '') + '.'}
                  {notice.state === 'dismissed' && 'Someone chose not to send this update.'}
                  {notice.state === 'superseded' && 'A later move replaced this update.'}
                  {(notice.state === 'blocked' || notice.state === 'failed') && (notice.decision_detail || 'This update could not be delivered.')}
                </div>
              )}

              {pending && blocked && (
                <div className="gtnt-banner gtnt-banner--amber">
                  <b>This email cannot be sent.</b> {email.blocked_reason}
                </div>
              )}
              {pending && email && !blocked && !email.sending_enabled && (
                <div className="gtnt-banner gtnt-banner--amber">
                  <b>Email is switched off on this server,</b> so nothing will be delivered.
                </div>
              )}

              {pending && email && (
                <div className="gtnt-email">
                  <div className="gtnt-meta">
                    <div><span>To</span><b>{email.to || 'No email address'}</b></div>
                    <div><span>Subject</span><b>{email.subject}</b></div>
                  </div>
                  {email.html && (
                    <iframe className="gtnt-preview" title="Email preview" sandbox="" srcDoc={
                      '<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:18px 20px;font:14px/1.6 Inter,system-ui,sans-serif;color:#161618}</style></head><body>' + email.html + '</body></html>'
                    } />
                  )}
                </div>
              )}

              {result && <div className={'gtnt-banner ' + (result.ok ? 'gtnt-banner--green' : 'gtnt-banner--rose')}>{result.text}</div>}
            </>
          )}
        </div>

        <div className="gtc-modal-foot gtnt-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={next}>
            {pending ? 'Decide later' : 'Close'}
          </button>
          <span className="gtnt-spacer" />
          {pending && (
            <>
              <button className="gtc-btn-ghost" disabled={busy || !!result} onClick={() => decide('dismiss')}>Don't send</button>
              <button className="gtc-primary" disabled={busy || !!result || !canSend} onClick={() => decide('send')}>
                {busy ? 'Working…' : 'Send email'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
